import { useInputValidation } from '@e2m-customer-portal/hooks';
import { isRequired, Nullable, Validation } from '@e2m-customer-portal/shared';
import { FormHelperTextProps, InputAdornment, OutlinedInputProps, TextField, TextFieldProps } from '@mui/material';
import { ChangeEvent, FocusEventHandler, ReactElement, ReactNode, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui-compat';
import { Popover } from 'ui/popover/Popover';

const useStyles = makeStyles()((theme) => ({
  warningLabel: {
    color: `${theme.palette.warning.main}`,
  },
  warningBorder: {
    borderColor: `${theme.palette.warning.main}`,
  },
}));

interface StandardTextInputProps {
  /**
   * Used to identify the input. Must be unique within the form or validated container.
   */
  name: string;

  /**
   * The value to display in the input.
   */
  value?: Nullable<string>;

  /**
   * Gets triggered whenever the value changes.
   * @param value
   */
  onChange?: (value: string) => void;

  onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;

  /**
   * Whether the input is required.
   */
  required?: Nullable<boolean>;

  /**
   * The coloring of the input outline. Colors are taken from the current theme.
   */
  color?: Nullable<'primary'>;

  /**
   * An optional input adornment that will be displayed at the end of the input.
   */
  endAdornment?: Nullable<ReactNode>;

  /**
   * An optional boolean that will change the field styling to the warning color.
   */
  warning?: boolean;

  /**
   * Additional rules that should be applied to this input.
   */
  rules?: Nullable<Validation<string>[]>;

  /**
   * If true, the label is displayed in an error state.
   */
  error?: boolean;
}

export type TextInputProps = Omit<
  TextFieldProps,
  'variant' | 'value' | 'color' | 'required' | 'error' | 'helperText' | 'onChange'
> &
  StandardTextInputProps;

export function TextInput({
  name,
  value,
  onChange,
  onBlur,
  color,
  required,
  endAdornment,
  warning,
  InputProps,
  rules: otherRules = [],
  ...rest
}: Readonly<TextInputProps>): ReactElement<TextInputProps> {
  const { classes } = useStyles();
  const [t] = useTranslation('validation');

  const rules = useMemo(() => {
    const result: Validation<string>[] = [];
    if (required) {
      result.push(isRequired(t('COMMON_REQUIRED')));
    }
    if (otherRules) {
      result.push(...otherRules);
    }
    return result;
  }, [t, required, otherRules]);

  const { handleBlur, error, message } = useInputValidation(name, rules, value, onBlur);

  const handleTextChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (onChange && event.target.value !== value) {
        onChange(event.target.value);
      }
    },
    [onChange, value],
  );

  const inputProps = useMemo<Partial<OutlinedInputProps>>(() => {
    const end = endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>;

    return {
      ...InputProps,
      id: name,
      endAdornment: end,
      classes:
        warning && !error
          ? {
              notchedOutline: classes.warningBorder,
            }
          : undefined,
    } as Partial<OutlinedInputProps>;
  }, [endAdornment, InputProps, name, warning, error, classes.warningBorder]);

  const labelProps = useMemo(
    () => ({
      htmlFor: name,
      classes:
        warning && !error
          ? {
              root: classes.warningLabel,
            }
          : undefined,
    }),
    [classes.warningLabel, error, name, warning],
  );

  const helperTextProps = useMemo(
    () =>
      ({
        role: 'alert',
        classes:
          warning && !error
            ? {
                // root: classes.warningLabel,
                // Unused, because we show a popover instead
                root: 'hidden',
              }
            : { error: 'hidden' },
      }) satisfies FormHelperTextProps,
    [classes.warningLabel, error, warning],
  );

  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <div className="w-full inline-flex flex-col group/cell">
      <TextField
        ref={ref}
        name={name}
        variant="outlined"
        value={value}
        color={color ?? 'primary'}
        required={required ?? false}
        error={error}
        InputProps={inputProps}
        InputLabelProps={labelProps}
        helperText={message ?? (warning && t('COMMON_WARNING_NOTE'))}
        FormHelperTextProps={helperTextProps}
        onChange={handleTextChange}
        onBlur={handleBlur}
        onClick={(e: { stopPropagation: () => unknown }) => e.stopPropagation()}
        {...rest}
      />
      {(error || warning) && (
        <Popover
          baseElement={ref.current}
          error={error && message ? message : undefined}
          warning={warning && message ? message : undefined}
        />
      )}
    </div>
  );
}
